/* eslint-disable no-restricted-imports */
import { Autocomplete as MUIAucomplete, TextField } from '@mui/material';

import { ReactNode, useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form';

import { I18nResources, useI18n } from '@app-velocity/i18n';

export type AutocompleteProps<T extends FieldValues> = {
  options: string[];
  control: Control<T>;
  formErrors: FieldErrors<T>;
  placeholder: I18nResources;
  name: Path<T>;
  renderOption: (option: string) => ReactNode;
  onFocus?: () => void;
  onBlur?: () => void;
  phCaptureId?: string;
};

export const Autocomplete = <T extends FieldValues>({
  name,
  control,
  formErrors,
  options,
  phCaptureId,
  onFocus,
  onBlur,
  renderOption,
}: AutocompleteProps<T>) => {
  const { t } = useI18n();

  const errorText = useMemo(() => {
    if (!formErrors) return null;
    if (!formErrors[name]) return null;
    if (!formErrors[name]!.message) return null;

    if (typeof formErrors[name]!.message !== 'string') return null;

    return formErrors![name]!.message;
  }, [formErrors, name]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <MUIAucomplete
          freeSolo
          disableClearable
          disablePortal
          id="challenge-description"
          options={options}
          onChange={(_, value) => {
            field.onChange(value);
          }}
          onInput={(value) => {
            field.onChange(value);
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          slotProps={{
            popper: {
              sx: {
                position: 'relative !important',
                transform: 'none !important',
              },
            },
            paper: {
              sx: {
                borderRadius: 2,
                padding: 2,
                borderTopRightRadius: 0,
                borderTopLeftRadius: 0,
                margin: 0,
                boxShadow: 'none',
                background: '#fff',
                border: '1px solid var(--stroke-stroke-primary-color)',
                borderTop: 'none',
                '.MuiAutocomplete-listbox': {
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '.MuiAutocomplete-option': {
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    padding: 2,
                    fontSize: 13,
                    minHeight: 'unset',
                    overflow: 'hidden',
                    fontWeight: 300,
                    color: 'var(--input-placeholder-color)',
                    svg: {
                      fill: 'var(--input-placeholder-color)',
                    },
                    '&[aria-selected="true"]': {
                      background: 'none',
                    },
                    '&:hover': {
                      color: 'var(--text-text-primary-color)',
                      svg: {
                        fill: 'var(--info-info-icon-primary-color)',
                      },
                    },
                  },
                },
              },
            },
          }}
          renderOption={({ key, ...optionProps }, option) => (
            <li key={key} {...optionProps}>
              {renderOption(option)}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              name="introMessage"
              fullWidth
              multiline
              inputProps={{
                ...params.inputProps,
                'data-ph-capture-attribute-id': phCaptureId,
              }}
              error={!!formErrors[name]}
              helperText={errorText as string | null}
              placeholder={t('prototypes:intro_message_placeholder')}
            />
          )}
        />
      )}
    />
  );
};
